import { AppstoreAddOutlined } from '@ant-design/icons';
import { Button, Form, Modal } from 'antd';
import { useEffect, useState } from 'react';

import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { useGetRoomsByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelRooms';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type {
  PackResponse,
  PackRoomResponse,
} from 'app/typings/core/admin/packs/pack';
import { ROOM_STATUS } from 'app/typings/core/admin/rooms/enum/roomStatus';
import type { HotelRoomResponse } from 'app/typings/core/admin/rooms/room';

import './AddRoomsModal.scss';
import { RoomListItem } from './RoomListItem';

type AddRoomsModalProps = {
  visible: boolean;
  onClose: () => void;
  title?: string;
};

export const AddRoomsModal = ({
  visible,
  onClose,
  title = 'Add Rooms',
}: AddRoomsModalProps) => {
  const form = Form.useFormInstance<
    Partial<PackResponse> & {
      packRooms: PackRoomResponse[];
    }
  >();
  const formValues = Form.useWatch([], form) ?? {};
  const { data: hotelRooms } = useGetRoomsByHotelId(formValues.hotelId);
  const { data: roomCategories } = useRoomCategories();

  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    setSelectedRoomIds(
      new Set((formValues.packRooms || []).map((r) => r.roomId))
    );
  }, [formValues.packRooms, visible]);

  const handleCheckboxChange = (roomId: number, checked: boolean) => {
    setSelectedRoomIds((prev) => {
      const copy = new Set(prev);

      if (checked) {
        copy.add(roomId);
      } else {
        copy.delete(roomId);
      }

      return copy;
    });
  };

  const handleSubmit = () => {
    if (!hotelRooms) {
      return;
    }

    const existingPackRooms = formValues.packRooms || [];
    let maxPriority = existingPackRooms.reduce(
      (max, r) => Math.max(max, r.priority ?? 0),
      -1
    );

    const newPackRooms: PackRoomResponse[] = hotelRooms.rooms
      .filter((room) => selectedRoomIds.has(room.id))
      .map((room) => {
        const existing = existingPackRooms.find(
          (pkgRoom) => pkgRoom.roomId === room.id
        );

        if (existing) {
          return existing;
        }

        return {
          roomId: room.id,
          priority: ++maxPriority,
        };
      });

    form.setFieldValue('packRooms', newPackRooms);
    form.setFields([{ name: 'packRooms', touched: true }]);
    form.validateFields();

    onClose();
  };

  const getRoomCategoryOrder = (categoryId?: number) => {
    return (
      roomCategories?.find((roomCategory) => roomCategory.id === categoryId)
        ?.roomOrder ?? Infinity
    );
  };

  const sortRooms = (rooms: HotelRoomResponse[]) =>
    [...rooms].sort((a, b) => {
      const aIsPublished = a.status === ROOM_STATUS.PUBLISHED;
      const bIsPublished = b.status === ROOM_STATUS.PUBLISHED;

      if (aIsPublished !== bIsPublished) {
        return aIsPublished ? -1 : 1;
      }

      const orderA = getRoomCategoryOrder(a.categoryId);
      const orderB = getRoomCategoryOrder(b.categoryId);

      return orderA - orderB;
    });

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      centered
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSubmit}>
          Save
        </Button>,
      ]}
      width={800}
      className="add-rooms-modal"
    >
      <div className="room-list">
        {hotelRooms?.rooms && hotelRooms?.rooms.length > 0 ? (
          sortRooms(hotelRooms.rooms).map((room: HotelRoomResponse) => (
            <RoomListItem
              key={room.id}
              room={room}
              selected={selectedRoomIds.has(room.id)}
              onToggle={handleCheckboxChange}
            />
          ))
        ) : (
          <EmptyState
            title="No rooms"
            description="No rooms configured"
            icon={<AppstoreAddOutlined />}
          />
        )}
      </div>
    </Modal>
  );
};
